import { useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';
import {
  Card,
  Table,
  Tag,
  Input,
  Space,
  Select,
  Button,
  Modal,
  Typography,
  message,
  Form,
  InputNumber,
  Tooltip,
  Tree,
  Spin,
  Alert,
  Timeline,
  Empty,
  Upload,
  List,
  DatePicker,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  CloudSyncOutlined,
  EditOutlined,
  HistoryOutlined,
  DownloadOutlined,
  UploadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api, http, LONG_RUNNING_TIMEOUT_MS, Page } from '../api/client';
import { formatImSyncAppliedMessage } from '../utils/dingtalk-sync-message';
import { useImChannel } from '../hooks/useImChannel';
import { useTalentPoolLabels } from '../contexts/TalentPoolLabelsContext';
import { UNASSIGNED_TALENT_POOL } from '../constants/talentPool';

const POOL_COLORS: Record<string, string> = {
  EXECUTIVE: 'red',
  MIDDLE_MGMT: 'volcano',
  BACKEND: 'blue',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
  SALES: 'gold',
  MEDICAL_OPS: 'green',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '在职',
  LEFT: '离职',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  LEFT: 'red',
};

const STATUS_OPTIONS = [
  { label: '在职', value: 'ACTIVE' },
  { label: '离职', value: 'LEFT' },
];

interface SyncResult {
  syncLogId: number;
  triggeredBy: string;
  added: Array<{ userid: string; name: string; mobile?: string; position?: string }>;
  changed: Array<{
    userid: string;
    before: { name: string | null; mobile: string | null };
    after: { name: string; mobile: string | null };
  }>;
  removed: Array<{ userid: string; name: string; employeeId: number }>;
  applied?: { created: number; updated: number; markedLeft: number };
  dryRun: boolean;
  scope?: 'ALL' | 'PARTIAL';
  scopeDeptCount?: number;
  scopeUserCount?: number;
}

interface DingtalkDept {
  deptId: number;
  name: string;
  parentId: number | null;
}

interface DingtalkDeptUser {
  userid: string;
  name: string;
  position: string | null;
  mobile: string | null;
}

const DEPT_KEY = (id: number) => `dept-${id}`;
const USER_KEY = (uid: string) => `user-${uid}`;
const isDeptKey = (k: Key) => typeof k === 'string' && k.startsWith('dept-');
const isUserKey = (k: Key) => typeof k === 'string' && k.startsWith('user-');
const parseDeptKey = (k: Key) => Number(String(k).slice(5));
const parseUserKey = (k: Key) => String(k).slice(5);

interface PromotionItem {
  id: number;
  yearMonth: string;
  levelText: string;
  amount: number | null;
  note?: string | null;
}

interface EmployeeRow {
  id: number;
  name: string;
  position?: string;
  deptPath?: string;
  sequence?: string;
  jobGrade?: string;
  jobLevel?: number | null;
  talentPool: string;
  port?: string | null;
  status: 'ACTIVE' | 'LEFT';
  directLeader?: { id: number; name: string } | null;
  directLeaderId?: number | null;
  mobile?: string | null;
  hireDate?: string | null;        // ISO 字符串；后端 Prisma DateTime 序列化结果
  promotions?: PromotionItem[];    // 倒序：最近一次在前
}

interface RosterImportResult {
  apply: boolean;
  added: Array<{ empNo: string; name: string }>;
  updated: Array<{ empNo: string; name: string; changes: string[] }>;
  unchanged: Array<{ empNo: string; name: string }>;
  invalid: Array<{ row: number; reason: string }>;
  appliedCounts?: { created: number; updated: number };
}

/** 把后端的 ISO 时间序列化成「YYYY-MM-DD」展示，空值显示「-」 */
function formatHireDate(v?: string | null): string {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 单条晋升的可读文案："2026-03 晋升2级 400元" */
function formatPromotion(p: PromotionItem): string {
  const amount = p.amount != null ? `${p.amount}元` : '';
  return [p.yearMonth, p.levelText, amount].filter(Boolean).join(' ');
}

function promotionCount(rows?: PromotionItem[] | null): number {
  return (rows ?? []).length;
}

function sumPromotionAmount(rows?: PromotionItem[] | null): number {
  return (rows ?? []).reduce((s, p) => s + (p.amount ?? 0), 0);
}

/** 须与 API `EmployeeService` 中 EXEC_POOL_ROSTER_NAME_ORDER 保持一致 */
const EXEC_ROSTER_PIN_ORDER = [
  '农俊',
  '郭天山',
  '梁钰珊',
  '陈丽媛',
  '蓝岚',
  '李承育',
  '罗生锃',
] as const;

function executivePinIndex(name: string | undefined): number | null {
  if (!name) return null;
  const i = (EXEC_ROSTER_PIN_ORDER as readonly string[]).indexOf(name);
  return i === -1 ? null : i;
}

function sortEmployeesByPoolOrder(rows: EmployeeRow[], poolOrder: string[]): EmployeeRow[] {
  const poolRank = (p: string) => {
    const i = poolOrder.indexOf(p);
    if (i >= 0) return i;
    if (p === UNASSIGNED_TALENT_POOL) return 9998;
    return 9999;
  };
  return [...rows].sort((a, b) => {
    const pr = poolRank(a.talentPool) - poolRank(b.talentPool);
    if (pr !== 0) return pr;
    if (a.talentPool === 'EXECUTIVE' && b.talentPool === 'EXECUTIVE') {
      const oa = executivePinIndex(a.name);
      const ob = executivePinIndex(b.name);
      if (oa != null || ob != null) {
        if (oa != null && ob != null) return oa - ob;
        if (oa != null) return -1;
        return 1;
      }
    }
    const dp = (a.deptPath || '').localeCompare(b.deptPath || '', 'zh-CN');
    if (dp !== 0) return dp;
    return (a.name || '').localeCompare(b.name || '', 'zh-CN');
  });
}

type PromotionFilter = 'ALL' | 'NONE';

const PROMOTION_FILTER_OPTIONS: { label: string; value: PromotionFilter }[] = [
  { label: '全部晋升情况', value: 'ALL' },
  { label: '未晋升', value: 'NONE' },
];

export function EmployeesPage() {
  const { getTalentPoolLabel, displayOrder } = useTalentPoolLabels();
  const { channel, label: imLabel } = useImChannel();
  const [q, setQ] = useState('');
  const [pool, setPool] = useState<string | undefined>();
  const [portFilter, setPortFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>('ACTIVE');
  const [deptFilter, setDeptFilter] = useState<string | undefined>();
  const [promotionFilter, setPromotionFilter] = useState<PromotionFilter>('ALL');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [q, pool, portFilter, statusFilter, deptFilter, promotionFilter]);
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [viewing, setViewing] = useState<EmployeeRow | null>(null);
  const [promotionEditing, setPromotionEditing] = useState<EmployeeRow | null>(null);
  const [editingPromotionId, setEditingPromotionId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<UploadFile | null>(null);
  const [importPreview, setImportPreview] = useState<RosterImportResult | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncMode, setSyncMode] = useState<'ALL' | 'PARTIAL'>('ALL');
  const [syncCheckedKeys, setSyncCheckedKeys] = useState<Key[]>([]);
  const [syncTreeData, setSyncTreeData] = useState<DataNode[]>([]);
  const [loadedDeptIds, setLoadedDeptIds] = useState<Set<number>>(() => new Set());
  const [loadingDeptIds, setLoadingDeptIds] = useState<Set<number>>(() => new Set());
  const [form] = Form.useForm();
  const [promotionForm] = Form.useForm();
  const queryClient = useQueryClient();

  const pageSize = 20;

  /** 一次拉齐当前筛选条件下的花名册（≤3000），在前端按人才池排序再分页，避免仅依赖后端分页时顺序错乱 */
  const { data, isFetching } = useQuery({
    queryKey: ['admin-employees', { q, pool, portFilter, statusFilter }],
    queryFn: () =>
      api.get<any, Page<EmployeeRow>>('/admin/employees', {
        params: {
          q,
          talentPool: pool,
          port: portFilter,
          status: statusFilter,
          page: 1,
          size: 3000,
        },
      }),
  });

  const { data: portOptionsRaw = [] } = useQuery({
    queryKey: ['admin-employees-ports'],
    queryFn: () => api.get<unknown, string[]>('/admin/employees/ports'),
    staleTime: 60_000,
  });

  /** 业绩端口下拉：只取「切档配置」里真正建档的端口（有 id），不含 _default、不含花名册脏值 */
  const { data: portCutoffs = [] } = useQuery({
    queryKey: ['port-cutoffs'],
    queryFn: () =>
      api.get('/admin/port-cutoffs') as Promise<
        Array<{ id: number | null; port: string }>
      >,
    staleTime: 60_000,
  });

  const configuredPortNames = useMemo(
    () =>
      portCutoffs
        .filter((c) => c.id != null && (c.port ?? '').trim() && c.port !== '_default')
        .map((c) => c.port.trim()),
    [portCutoffs],
  );

  const portFilterOptions = useMemo(() => {
    // 筛选器可含历史脏值，方便捞人；编辑下拉不用这些
    const names = new Set<string>([...configuredPortNames, ...portOptionsRaw]);
    return [
      { label: '未填写', value: '__unset__' },
      ...[...names]
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((p) => ({ label: p, value: p })),
    ];
  }, [configuredPortNames, portOptionsRaw]);

  const talentPoolEditOptions = useMemo(() => {
    const codes = new Set<string>(displayOrder);
    codes.add(UNASSIGNED_TALENT_POOL);
    if (editing?.talentPool) codes.add(editing.talentPool);
    return [...codes].map((code) => ({
      label: getTalentPoolLabel(code),
      value: code,
    }));
  }, [displayOrder, editing?.talentPool, getTalentPoolLabel]);

  const portEditOptions = useMemo(() => {
    const names = new Set<string>(configuredPortNames);
    // 仅当当前人挂着未建档脏值时保留一项，方便清掉或改选正式端口
    const current = editing?.port?.trim();
    if (current && current !== '_default' && !names.has(current)) {
      names.add(current);
    }
    return [...names]
      .sort((a, b) => a.localeCompare(b, 'zh-CN'))
      .map((p) => ({
        label: configuredPortNames.includes(p) ? p : `${p}（未在切档配置建档）`,
        value: p,
      }));
  }, [configuredPortNames, editing?.port]);

  const sortedAll = useMemo(
    () => sortEmployeesByPoolOrder(data?.items ?? [], displayOrder),
    [data?.items, displayOrder],
  );

  /** 部门下拉的选项：从当前已加载花名册里去重，按中文排序 */
  const deptOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of sortedAll) {
      if (e.deptPath) set.add(e.deptPath);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'zh-CN'))
      .map((d) => ({ label: d, value: d }));
  }, [sortedAll]);

  /** 应用部门 + 晋升情况筛选；其它筛选已经在后端 query 里做掉了 */
  const filteredAll = useMemo(() => {
    return sortedAll.filter((row) => {
      if (deptFilter && row.deptPath !== deptFilter) return false;
      if (promotionFilter === 'NONE' && promotionCount(row.promotions) > 0) return false;
      return true;
    });
  }, [sortedAll, deptFilter, promotionFilter]);

  const tableItems = useMemo(
    () => filteredAll.slice((page - 1) * pageSize, page * pageSize),
    [filteredAll, page],
  );

  // 编辑弹框需要选直属上级，拉一份候选名单（在职员工）
  const { data: leaderCandidates } = useQuery({
    queryKey: ['admin-employees-all'],
    queryFn: () =>
      api.get<any, Page<EmployeeRow>>('/admin/employees', {
        params: { status: 'ACTIVE', page: 1, size: 3000 },
      }),
    enabled: editing !== null,
  });

  const leaderOptions = useMemo(
    () =>
      sortEmployeesByPoolOrder(leaderCandidates?.items || [], displayOrder).map((e) => ({
        label: `${e.name}${e.position ? ` · ${e.position}` : ''}`,
        value: e.id,
      })),
    [leaderCandidates, displayOrder],
  );

  const syncMutation = useMutation({
    mutationFn: (params: { apply: boolean; deptIds?: number[]; userIds?: string[] }) =>
      api.post<any, SyncResult>('/admin/employees/sync-im', params, {
        timeout: LONG_RUNNING_TIMEOUT_MS,
      }),
  });

  // IM 部门列表只在打开「按范围同步」对话框时拉取
  const {
    data: dingtalkDepts,
    isFetching: deptsLoading,
    error: deptsError,
    refetch: refetchDepts,
  } = useQuery<{ items: DingtalkDept[] }>({
    queryKey: ['im-depts', channel],
    queryFn: () =>
      api.get<any, { items: DingtalkDept[] }>('/admin/employees/im-departments', {
        timeout: LONG_RUNNING_TIMEOUT_MS,
      }),
    enabled: false,
    retry: false,
  });

  // 部门树（不含成员）。成员节点会在用户展开部门时通过 loadData 异步追加。
  // checkStrictly 模式下：勾「部门节点」= 整个部门进白名单；勾「人员叶子」= 仅这一个人。两者相互独立。
  const deptOnlyTreeData: DataNode[] = useMemo(() => {
    const items = dingtalkDepts?.items ?? [];
    if (items.length === 0) return [];
    const byParent = new Map<number | null, DingtalkDept[]>();
    for (const d of items) {
      const k = d.parentId;
      const list = byParent.get(k) ?? [];
      list.push(d);
      byParent.set(k, list);
    }
    const roots = items.filter(
      (d) => d.parentId === null || !items.some((x) => x.deptId === d.parentId),
    );
    const build = (d: DingtalkDept): DataNode => ({
      key: DEPT_KEY(d.deptId),
      title: d.name,
      // 部门节点永远不是叶子：要么有子部门，要么 loadData 时会追加成员
      isLeaf: false,
      children: (byParent.get(d.deptId) ?? []).map(build),
    });
    return roots.map(build);
  }, [dingtalkDepts]);

  useEffect(() => {
    setSyncTreeData(deptOnlyTreeData);
    setLoadedDeptIds(new Set());
    setLoadingDeptIds(new Set());
  }, [deptOnlyTreeData]);

  /**
   * 树索引：每个部门 → 所有后代部门 deptId / 所有「已展示的」后代成员 userid。
   * 因为成员是懒加载的，所以这张表会随 syncTreeData 变化（部门展开后才有该部门的成员）。
   * 用途：
   *   - 勾父部门 = 把所有子部门 dept-key + 已展开的成员 user-key 都点亮（取消时反之）
   *   - 即使父部门当前已勾、再展开子部门时也可用 descendantUsers 同步新出现的成员
   */
  const treeIndex = useMemo(() => {
    const descendantDepts = new Map<number, number[]>();
    const descendantUsers = new Map<number, string[]>();
    const walk = (nodes: DataNode[]): { depts: number[]; users: string[] } => {
      const allDepts: number[] = [];
      const allUsers: string[] = [];
      for (const n of nodes) {
        if (typeof n.key !== 'string') continue;
        if (n.key.startsWith('dept-')) {
          const id = parseDeptKey(n.key);
          const sub = walk(n.children ?? []);
          descendantDepts.set(id, sub.depts);
          descendantUsers.set(id, sub.users);
          allDepts.push(id, ...sub.depts);
          allUsers.push(...sub.users);
        } else if (n.key.startsWith('user-')) {
          allUsers.push(parseUserKey(n.key));
        }
      }
      return { depts: allDepts, users: allUsers };
    };
    walk(syncTreeData);
    return { descendantDepts, descendantUsers };
  }, [syncTreeData]);

  // 异步拉某部门下成员，挂到树节点上。已加载过的不重复加载。
  const loadDeptUsers = async (deptId: number) => {
    if (loadedDeptIds.has(deptId) || loadingDeptIds.has(deptId)) return;
    setLoadingDeptIds((prev) => {
      const next = new Set(prev);
      next.add(deptId);
      return next;
    });
    try {
      const res = await api.get<any, { items: DingtalkDeptUser[] }>(
        `/admin/employees/im-departments/${deptId}/users`,
      );
      const userNodes: DataNode[] = (res.items ?? []).map((u) => ({
        key: USER_KEY(u.userid),
        title: u.position ? `${u.name} · ${u.position}` : u.name,
        isLeaf: true,
      }));
      setSyncTreeData((prev) => appendUserNodes(prev, deptId, userNodes));
      setLoadedDeptIds((prev) => {
        const next = new Set(prev);
        next.add(deptId);
        return next;
      });
      // 若当前部门已被勾选（可能是父部门联动而来），把新展开出来的成员叶子也一并勾上，
      // 这样 UI 才能体现「勾父部门 = 含已展开的成员」的视觉语义。
      setSyncCheckedKeys((prev) => {
        if (!prev.includes(DEPT_KEY(deptId))) return prev;
        const set = new Set<Key>(prev);
        for (const u of res.items ?? []) {
          set.add(USER_KEY(u.userid));
        }
        return Array.from(set);
      });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '加载部门成员失败');
    } finally {
      setLoadingDeptIds((prev) => {
        const next = new Set(prev);
        next.delete(deptId);
        return next;
      });
    }
  };

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<EmployeeRow> }) =>
      api.patch<any, EmployeeRow>(`/admin/employees/${payload.id}`, payload.data),
    onSuccess: () => {
      message.success('保存成功');
      queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
      queryClient.invalidateQueries({ queryKey: ['admin-employees-ports'] });
      queryClient.invalidateQueries({ queryKey: ['port-cutoffs'] });
      setEditing(null);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    },
  });

  const savePromotionMutation = useMutation({
    mutationFn: (payload: {
      employeeId: number;
      promotionId?: number | null;
      data: { yearMonth: string; levelText: string; amount?: number | null; note?: string | null };
    }) => {
      const body = {
        yearMonth: payload.data.yearMonth,
        levelText: payload.data.levelText.trim(),
        amount: payload.data.amount ?? null,
        note: payload.data.note?.trim() || null,
      };
      if (payload.promotionId) {
        return api.patch(
          `/admin/employees/${payload.employeeId}/promotions/${payload.promotionId}`,
          body,
        );
      }
      return api.post(`/admin/employees/${payload.employeeId}/promotions`, body);
    },
    onSuccess: async (_row, vars) => {
      message.success('晋升记录已保存');
      await queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
      const fresh = await queryClient.fetchQuery({
        queryKey: ['admin-employees', { q, pool, portFilter, statusFilter }],
        queryFn: () =>
          api.get<any, Page<EmployeeRow>>('/admin/employees', {
            params: {
              q,
              talentPool: pool,
              port: portFilter,
              status: statusFilter,
              page: 1,
              size: 3000,
            },
          }),
      });
      const updated = fresh.items.find((e) => e.id === vars.employeeId);
      if (updated) setPromotionEditing(updated);
      setEditingPromotionId(null);
      promotionForm.resetFields();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    },
  });

  const openPromotionEdit = (row: EmployeeRow) => {
    setPromotionEditing(row);
    setEditingPromotionId(null);
    promotionForm.resetFields();
  };

  const startEditPromotionLine = (p: PromotionItem) => {
    setEditingPromotionId(p.id);
    promotionForm.setFieldsValue({
      yearMonth: dayjs(p.yearMonth, 'YYYY-MM'),
      levelText: p.levelText,
      amount: p.amount ?? undefined,
      note: p.note ?? '',
    });
  };

  const handleSavePromotion = async () => {
    if (!promotionEditing) return;
    const values = await promotionForm.validateFields();
    await savePromotionMutation.mutateAsync({
      employeeId: promotionEditing.id,
      promotionId: editingPromotionId,
      data: {
        yearMonth: values.yearMonth.format('YYYY-MM'),
        levelText: values.levelText,
        amount: values.amount,
        note: values.note,
      },
    });
  };

  const openSyncDialog = () => {
    setSyncMode('ALL');
    setSyncCheckedKeys([]);
    setSyncOpen(true);
  };

  // 把勾选的 keys 拆成 deptIds / userIds，并去重。
  const splitCheckedKeys = (keys: Key[]) => {
    const deptIds = Array.from(
      new Set(keys.filter(isDeptKey).map(parseDeptKey)),
    );
    const userIds = Array.from(
      new Set(keys.filter(isUserKey).map(parseUserKey)),
    );
    return { deptIds, userIds };
  };

  const runSync = async () => {
    const { deptIds, userIds } = splitCheckedKeys(syncCheckedKeys);
    if (syncMode === 'PARTIAL' && deptIds.length === 0 && userIds.length === 0) {
      message.warning('按范围同步时，请至少勾选一个部门或一名员工');
      return;
    }
    setSyncOpen(false);
    try {
      const params = {
        apply: false,
        deptIds: syncMode === 'PARTIAL' && deptIds.length > 0 ? deptIds : undefined,
        userIds: syncMode === 'PARTIAL' && userIds.length > 0 ? userIds : undefined,
      };
      const dry = await syncMutation.mutateAsync(params);
      const total = dry.added.length + dry.changed.length + dry.removed.length;
      const scopeText =
        syncMode === 'PARTIAL'
          ? [
              deptIds.length > 0 ? `${deptIds.length} 个部门` : null,
              userIds.length > 0 ? `${userIds.length} 名员工` : null,
            ]
              .filter(Boolean)
              .join(' + ')
          : '';
      if (total === 0) {
        if (syncMode === 'ALL') {
          Modal.confirm({
            title: '刷新组织架构',
            width: 520,
            okText: '刷新组织架构',
            cancelText: '取消',
            content: (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                员工花名册与{imLabel}一致，无新增/变更/离职。是否仍同步<strong>部门树</strong>并更新所有员工的
                <strong>部门路径</strong>？（报表中心全员目录、部门筛选依赖此数据）
              </Typography.Paragraph>
            ),
            onOk: async () => {
              const applied = await syncMutation.mutateAsync({ apply: true });
              message.success(formatImSyncAppliedMessage(imLabel, applied.applied));
              queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
              queryClient.invalidateQueries({ queryKey: ['admin-employees-ports'] });
            },
          });
        } else {
          message.success(`所选范围（${scopeText}）${imLabel}数据与本地一致，无需同步`);
        }
        return;
      }
      Modal.confirm({
        title:
          syncMode === 'PARTIAL'
            ? `按范围同步（${scopeText}）`
            : `从${imLabel}全量同步`,
        width: 640,
        okText: `确认入库（共 ${total} 条变更）`,
        cancelText: '只看不动',
        content: (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              试运行结果，确认无误后入库。新增员工的「人才池」默认为「未分配」，「序列」默认为 PRO，入库后请到花名册手工维护。
              {syncMode === 'PARTIAL'
                ? `按范围同步：所选范围以外的「${imLabel}员工」会被标记为「离职」（保留 ADMIN 与手工建的无外部 ID 员工）。`
                : `全公司同步：${imLabel}通讯录里没有的在职员工一律标记为「离职」（含本地无外部 ID 的老数据/补录人员）；${imLabel}里没有的部门也会停用。`}
            </Typography.Paragraph>
            {dry.removed.length > 0 ? (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message={`本次将把 ${dry.removed.length} 名员工标记为「离职」`}
                description="请认真核对下方「候选离职」名单，确认无误再点入库。"
              />
            ) : null}
            <SyncDiffPreview diff={dry} imLabel={imLabel} />
          </div>
        ),
        onOk: async () => {
          const applied = await syncMutation.mutateAsync({ ...params, apply: true });
          message.success(formatImSyncAppliedMessage(imLabel, applied.applied));
          queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
          queryClient.invalidateQueries({ queryKey: ['admin-employees-ports'] });
        },
      });
    } catch (e: any) {
      Modal.error({
        title: `${imLabel}同步失败`,
        width: 540,
        content: (
          <div>
            <p>{e?.response?.data?.message || e?.message || '未知错误'}</p>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              {channel === 'wecom' ? (
                <>
                  请确认企微应用已启用，Secret 正确，且已开启「通讯录」只读权限；可信 IP 需包含正式服出口 IP。
                </>
              ) : (
                <>
                  如错误为「请求的部门id不在授权范围内」，请到{' '}
                  <a href="https://open-dev.dingtalk.com" target="_blank" rel="noreferrer">
                    钉钉开发者后台
                  </a>{' '}
                  给本应用授「通讯录读权限」并把「应用管理范围」设为全公司。
                </>
              )}
            </Typography.Paragraph>
          </div>
        ),
      });
    }
  };

  const handleModeChange = (mode: 'ALL' | 'PARTIAL') => {
    setSyncMode(mode);
    if (mode === 'PARTIAL' && !dingtalkDepts && !deptsLoading) {
      refetchDepts();
    }
  };

  const checkedSummary = useMemo(() => splitCheckedKeys(syncCheckedKeys), [syncCheckedKeys]);

  const openEdit = (row: EmployeeRow) => {
    setEditing(row);
  };

  /** 当前筛选条件 → URL params，与列表 useQuery 保持一致 */
  const buildExportParams = (): Record<string, string | undefined> => ({
    q: q || undefined,
    talentPool: pool || undefined,
    port: portFilter || undefined,
    status: statusFilter || undefined,
  });

  /**
   * 通用 Excel 下载：解决浏览器把后端 JSON 报错当 .xlsx 落盘的问题——先看 Content-Type，再决定是否落盘。
   * 用 axios http（非 envelope）实例，绕开自动解包 {ok,data} 的拦截器。
   */
  const downloadXlsx = async (
    path: string,
    filename: string,
    params?: Record<string, string | undefined>,
  ) => {
    const r = await http.get(path, {
      params,
      responseType: 'blob',
    });
    const ct = r.headers?.['content-type'] ?? '';
    if (!String(ct).includes('spreadsheetml')) {
      let text = '';
      try {
        text = await (r.data as Blob).text();
      } catch {
        /* ignore */
      }
      throw new Error(`后端未返回 Excel：${text.slice(0, 200) || '未知错误'}`);
    }
    const blob = new Blob([r.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadRosterExcel = async () => {
    setExportBusy(true);
    try {
      await downloadXlsx(
        '/admin/employees/export.xlsx',
        `花名册-${new Date().toISOString().slice(0, 10)}.xlsx`,
        buildExportParams(),
      );
      message.success('已下载');
    } catch (e: any) {
      message.error(`导出失败：${e?.response?.status ?? ''} ${e?.message ?? '未知错误'}`);
    } finally {
      setExportBusy(false);
    }
  };

  const downloadImportTemplate = async () => {
    setTemplateBusy(true);
    try {
      await downloadXlsx(
        '/admin/employees/import-template.xlsx',
        '花名册导入模版.xlsx',
      );
      message.success('模版已下载');
    } catch (e: any) {
      message.error(`模版下载失败：${e?.response?.status ?? ''} ${e?.message ?? '未知错误'}`);
    } finally {
      setTemplateBusy(false);
    }
  };

  const openImportDialog = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportOpen(true);
  };

  const callImport = async (apply: boolean) => {
    if (!importFile?.originFileObj) {
      message.warning('请先选择 Excel(.xlsx) 或 CSV(.csv) 文件');
      return;
    }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile.originFileObj as Blob);
      const result = (await api.post<RosterImportResult>(
        `/admin/employees/import-excel?apply=${apply ? 'true' : 'false'}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )) as unknown as RosterImportResult;
      setImportPreview(result);
      if (apply) {
        const c = result.appliedCounts;
        message.success(
          `导入完成：新增 ${c?.created ?? 0} / 更新 ${c?.updated ?? 0}${
            result.invalid.length ? ` / 失败 ${result.invalid.length}` : ''
          }`,
        );
        queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
      queryClient.invalidateQueries({ queryKey: ['admin-employees-ports'] });
      } else {
        message.info(
          `预览完成：新增 ${result.added.length} / 更新 ${result.updated.length} / 无变化 ${result.unchanged.length} / 警告 ${result.invalid.length}`,
        );
      }
    } catch (e: any) {
      message.error(
        `${apply ? '导入' : '预览'}失败：${e?.response?.data?.message ?? e?.message ?? '未知错误'}`,
      );
    } finally {
      setImportBusy(false);
    }
  };

  const editInitialValues = useMemo(() => {
    if (!editing) return undefined;
    return {
      name: editing.name,
      deptPath: editing.deptPath,
      position: editing.position,
      jobGrade: editing.jobGrade,
      jobLevel: editing.jobLevel ?? null,
      talentPool: editing.talentPool,
      directLeaderId: editing.directLeader?.id ?? editing.directLeaderId ?? null,
      port: editing.port?.trim() ? editing.port.trim() : undefined,
      status: editing.status,
    };
  }, [editing]);

  const handleSave = async () => {
    if (!editing) return;
    const values = await form.validateFields();
    updateMutation.mutate({
      id: editing.id,
      data: {
        ...values,
        // 清空端口 → 空串入库，切档时走 _default
        port: values.port?.trim() ? values.port.trim() : '',
      },
    });
  };

  return (
    <div>
      <div className="tg-page-title">花名册</div>
      <Card styles={{ body: { paddingBlock: 12, paddingInline: 16 } }}>
        <Space style={{ marginBottom: 10 }} wrap size={[8, 8]}>
          <Input.Search
            placeholder="姓名 / 职位"
            allowClear
            onSearch={(v) => {
              setQ(v);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder="人才池"
            style={{ width: 160 }}
            options={displayOrder.map((k) => ({
              label: getTalentPoolLabel(k),
              value: k,
            }))}
            onChange={(v) => {
              setPool(v);
              setPage(1);
            }}
          />
          <Select
            allowClear
            showSearch
            placeholder="端口"
            style={{ width: 140 }}
            value={portFilter}
            options={portFilterOptions}
            optionFilterProp="label"
            onChange={(v) => {
              setPortFilter(v);
              setPage(1);
            }}
          />
          <Select
            placeholder="在职状态"
            style={{ width: 130 }}
            value={statusFilter}
            options={[
              { label: '在职', value: 'ACTIVE' },
              { label: '离职', value: 'LEFT' },
              { label: '全部', value: undefined as any },
            ]}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            allowClear={false}
          />
          <Select
            allowClear
            showSearch
            placeholder="部门"
            style={{ width: 200 }}
            value={deptFilter}
            options={deptOptions}
            optionFilterProp="label"
            onChange={(v) => {
              setDeptFilter(v);
              setPage(1);
            }}
          />
          <Select
            placeholder="晋升情况"
            style={{ width: 190 }}
            value={promotionFilter}
            options={PROMOTION_FILTER_OPTIONS}
            onChange={(v) => {
              setPromotionFilter(v);
              setPage(1);
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<CloudSyncOutlined />}
            loading={syncMutation.isPending}
            onClick={openSyncDialog}
          >
            从{imLabel}同步
          </Button>
          <Tooltip title="支持 Excel(.xlsx) / CSV(.csv)；按姓名匹配（同名须工号），预览后再入库">
            <Button
              type="primary"
              ghost
              icon={<UploadOutlined />}
              onClick={openImportDialog}
            >
              导入 Excel/CSV
            </Button>
          </Tooltip>
          <Tooltip title="按当前筛选条件导出花名册 Excel（含工号、序列、人才池等全字段）">
            <Button
              type="primary"
              ghost
              icon={<DownloadOutlined />}
              onClick={downloadRosterExcel}
              loading={exportBusy}
            >
              导出 Excel
            </Button>
          </Tooltip>
          <Tooltip title="下载导入用的空模版（带表头 + 示例行 + 字段说明 sheet）">
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadImportTemplate}
              loading={templateBusy}
            >
              下载模版
            </Button>
          </Tooltip>
        </Space>
        <Table<EmployeeRow>
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={tableItems}
          pagination={{
            current: page,
            pageSize,
            total: filteredAll.length,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
            size: 'small',
            showTotal: (t) => `共 ${t} 人`,
          }}
          columns={[
            {
              title: '序号',
              key: '__index',
              width: 52,
              fixed: 'left',
              align: 'center',
              render: (_, __, idx) => (page - 1) * pageSize + idx + 1,
            },
            { title: '姓名', dataIndex: 'name', width: 88, fixed: 'left' },
            {
              title: '部门',
              dataIndex: 'deptPath',
              width: 136,
              render: (v: string | undefined) => {
                const text = v ?? '-';
                return (
                  <Typography.Text
                    style={{ width: '100%', marginBottom: 0, fontSize: 12 }}
                    ellipsis={{ tooltip: text !== '-' ? text : false }}
                  >
                    {text}
                  </Typography.Text>
                );
              },
            },
            {
              title: '职位',
              dataIndex: 'position',
              width: 152,
              render: (v: string | undefined) => {
                const text = v ?? '-';
                return (
                  <Typography.Text
                    style={{ width: '100%', marginBottom: 0, fontSize: 12 }}
                    ellipsis={{ tooltip: text !== '-' ? text : false }}
                  >
                    {text}
                  </Typography.Text>
                );
              },
            },
            {
              title: '入职时间',
              dataIndex: 'hireDate',
              width: 108,
              render: (v) => (
                <span style={{ whiteSpace: 'nowrap' }}>{formatHireDate(v)}</span>
              ),
            },
            { title: '职级', dataIndex: 'jobGrade', width: 90 },
            {
              title: '等级',
              dataIndex: 'jobLevel',
              width: 80,
              render: (v) => (v ? `${v}级` : '-'),
            },
            {
              title: '人才池',
              dataIndex: 'talentPool',
              width: 130,
              sorter: false,
              render: (v) => <Tag color={POOL_COLORS[v] || 'default'}>{getTalentPoolLabel(v)}</Tag>,
            },
            {
              title: '端口',
              dataIndex: 'port',
              width: 100,
              render: (v: string | null | undefined) => {
                const text = v?.trim() || '-';
                return (
                  <Typography.Text
                    style={{ width: '100%', marginBottom: 0, fontSize: 12 }}
                    ellipsis={{ tooltip: text !== '-' ? text : false }}
                  >
                    {text}
                  </Typography.Text>
                );
              },
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 80,
              render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{STATUS_LABELS[v] || v}</Tag>,
            },
            {
              title: '直属上级',
              dataIndex: ['directLeader', 'name'],
              width: 100,
              render: (v: string | undefined) => {
                const text = v ?? '-';
                return (
                  <Typography.Text
                    style={{ width: '100%', marginBottom: 0 }}
                    ellipsis={{ tooltip: text !== '-' ? text : false }}
                  >
                    {text}
                  </Typography.Text>
                );
              },
            },
            {
              title: '历史晋升',
              key: 'promotions',
              width: 100,
              render: (_, row) => {
                const total = promotionCount(row.promotions);
                if (total === 0) {
                  return (
                    <Tooltip title="暂无晋升记录">
                      <Tag style={{ cursor: 'default', color: '#bbb', borderStyle: 'dashed' }}>
                        未晋升
                      </Tag>
                    </Tooltip>
                  );
                }
                return (
                  <Tag
                    color="geekblue"
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setViewing(row)}
                  >
                    {total} 次
                  </Tag>
                );
              },
            },
            {
              title: '操作',
              key: 'actions',
              width: 160,
              fixed: 'right',
              render: (_, row) => (
                <Space size={0}>
                  <Button
                    size="small"
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(row)}
                  >
                    编辑
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    icon={<HistoryOutlined />}
                    onClick={() => openPromotionEdit(row)}
                  >
                    历史晋升
                  </Button>
                </Space>
              ),
            },
          ]}
          scroll={{ x: 1340 }}
        />
      </Card>

      <Modal
        open={viewing !== null}
        onCancel={() => setViewing(null)}
        footer={null}
        width={560}
        destroyOnClose
        title={
          viewing ? (
            <Space size={8} wrap>
              <span style={{ fontWeight: 600 }}>{viewing.name}</span>
              {viewing.position ? (
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {viewing.position}
                </Typography.Text>
              ) : null}
              <Tag color="geekblue">
                共 {(viewing.promotions ?? []).length} 次晋升
              </Tag>
              {sumPromotionAmount(viewing.promotions) > 0 ? (
                <Tag color="purple">累计 +{sumPromotionAmount(viewing.promotions)} 元</Tag>
              ) : null}
            </Space>
          ) : (
            '晋升明细'
          )
        }
      >
        {viewing &&
          ((viewing.promotions ?? []).length === 0 ? (
            <Empty description="暂无晋升记录" />
          ) : (
            <Timeline
              style={{ marginTop: 8 }}
              items={(viewing.promotions ?? []).map((p) => ({
                color: 'blue',
                children: (
                  <div style={{ lineHeight: 1.65 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {formatPromotion(p)}
                    </div>
                    {p.note ? (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        · {p.note}
                      </div>
                    ) : null}
                  </div>
                ),
              }))}
            />
          ))}
      </Modal>

      <Modal
        title={
          promotionEditing
            ? `历史晋升 - ${promotionEditing.name}${editingPromotionId ? '（编辑）' : '（新增）'}`
            : '历史晋升'
        }
        open={promotionEditing !== null}
        onCancel={() => {
          setPromotionEditing(null);
          setEditingPromotionId(null);
          promotionForm.resetFields();
        }}
        onOk={handleSavePromotion}
        okText="保存"
        confirmLoading={savePromotionMutation.isPending}
        width={640}
        destroyOnClose
      >
        {promotionEditing ? (
          <>
            <List
              size="small"
              header="已有记录（点击编辑）"
              dataSource={promotionEditing.promotions ?? []}
              locale={{ emptyText: '暂无，可在下方新增' }}
              renderItem={(p) => (
                <List.Item
                  actions={[
                    <Button type="link" size="small" key="edit" onClick={() => startEditPromotionLine(p)}>
                      编辑
                    </Button>,
                  ]}
                >
                  {formatPromotion(p)}
                </List.Item>
              )}
              style={{ marginBottom: 16, maxHeight: 200, overflow: 'auto' }}
            />
            <Form form={promotionForm} layout="vertical" preserve={false}>
              <Form.Item
                label="晋升月份"
                name="yearMonth"
                rules={[{ required: true, message: '请选择月份' }]}
              >
                <DatePicker picker="month" style={{ width: '100%' }} placeholder="YYYY-MM" />
              </Form.Item>
              <Form.Item
                label="级数文案"
                name="levelText"
                rules={[{ required: true, message: '请填写级数' }]}
              >
                <Input placeholder="如 晋升1级" />
              </Form.Item>
              <Form.Item label="金额（元）" name="amount">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="可选" />
              </Form.Item>
              <Form.Item label="备注" name="note">
                <Input.TextArea rows={2} placeholder="可选" />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Modal>

      <Modal
        title={editing ? `编辑员工 - ${editing.name}` : '编辑员工'}
        open={editing !== null}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={updateMutation.isPending}
        width={560}
        destroyOnClose
      >
        <Form
          key={editing?.id ?? 'empty'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={editInitialValues}
        >
          <Form.Item label="姓名" name="name">
            <Input disabled />
          </Form.Item>
          <Form.Item label="部门" name="deptPath">
            <Input placeholder="如 广州瑞小美/护理部" />
          </Form.Item>
          <Form.Item label="职位" name="position">
            <Input />
          </Form.Item>
          <Space.Compact block style={{ marginBottom: 24 }}>
            <Form.Item label="职级" name="jobGrade" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="如 P3 / M2" />
            </Form.Item>
            <Form.Item label="等级" name="jobLevel" style={{ width: 140, marginBottom: 0, marginLeft: 12 }}>
              <InputNumber min={1} max={5} placeholder="1-5" style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            label="人才池"
            name="talentPool"
            rules={[{ required: true, message: '请选择人才池' }]}
            extra="评价关系按人才池匹配素质模板；钉钉同步新人默认为「未分配」，需在此划入正式池。"
          >
            <Select
              showSearch
              placeholder="选择人才池"
              optionFilterProp="label"
              options={talentPoolEditOptions}
            />
          </Form.Item>
          <Form.Item label="直属上级" name="directLeaderId">
            <Select
              showSearch
              allowClear
              placeholder="搜索姓名 / 职位"
              optionFilterProp="label"
              options={leaderOptions}
              loading={!leaderCandidates}
            />
          </Form.Item>
          <Form.Item
            label="业务端口"
            name="port"
            extra={
              configuredPortNames.length === 0 ? (
                <span>
                  请先到侧边栏「业绩端口 · 切档配置」添加端口，再回来选择。留空则切档走 _default 兜底。
                </span>
              ) : (
                <span>
                  选项来自「业绩端口 · 切档配置」。留空则切档走 _default 兜底。
                </span>
              )
            }
          >
            <Select
              showSearch
              allowClear
              placeholder={
                configuredPortNames.length === 0
                  ? '暂无已配置端口，可留空'
                  : '选择业务端口'
              }
              optionFilterProp="label"
              options={portEditOptions}
              notFoundContent={
                configuredPortNames.length === 0
                  ? '请先在「业绩端口 · 切档配置」添加端口'
                  : undefined
              }
            />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`从${imLabel}同步`}
        open={syncOpen}
        onCancel={() => setSyncOpen(false)}
        onOk={runSync}
        okText="开始试运行"
        cancelText="取消"
        confirmLoading={syncMutation.isPending}
        width={620}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="同步范围">
            <Select
              value={syncMode}
              onChange={handleModeChange}
              options={[
                { label: `全公司同步（${imLabel}里所有部门 → 本地）`, value: 'ALL' },
                { label: '按范围同步（勾选部门 / 具体人员）', value: 'PARTIAL' },
              ]}
            />
          </Form.Item>
          {syncMode === 'PARTIAL' ? (
            <Form.Item
              label="选择要同步的范围"
              extra={
                <span>
                  勾选「<strong>部门</strong>」=该部门所有成员加入白名单（自动勾选全部子部门 + 已展开的成员；可再手动取消个别）；
                  勾选「<strong>人员</strong>」=仅这一名员工加入白名单。
                  两者并集即本次白名单。本地非 ADMIN 且有外部 ID、但不在白名单的员工会被标记为「离职」。
                  展开部门可看到并单独勾选部门成员。
                </span>
              }
            >
              {deptsError ? (
                <Alert
                  type="error"
                  showIcon
                  message={`拉取${imLabel}部门列表失败`}
                  description={
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        {(deptsError as any)?.response?.data?.message ||
                          (deptsError as any)?.message ||
                          '未知错误'}
                      </div>
                      <Button size="small" onClick={() => refetchDepts()}>
                        重试
                      </Button>
                    </div>
                  }
                />
              ) : deptsLoading || !dingtalkDepts ? (
                <div style={{ padding: '24px 0', textAlign: 'center' }}>
                  <Spin tip={`加载${imLabel}部门…`} />
                </div>
              ) : syncTreeData.length === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`${imLabel}返回的部门列表为空`}
                  description={
                    channel === 'wecom'
                      ? '请确认应用已开启「通讯录」只读权限，且应用可见范围覆盖目标部门。'
                      : '可能本应用没有「通讯录读权限」或管理范围未设为全公司。'
                  }
                />
              ) : (
                <div
                  style={{
                    maxHeight: 360,
                    overflow: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    padding: 8,
                  }}
                >
                  <Tree
                    checkable
                    checkStrictly
                    selectable={false}
                    treeData={syncTreeData}
                    checkedKeys={syncCheckedKeys}
                    onExpand={(_, info) => {
                      // 用 onExpand 触发懒加载，而不是 antd 的 loadData。
                      // 因为 loadData 只在节点 children 为空时触发，但很多部门已经有子部门作为
                      // children（钉钉部门树初始化就带上了），导致它们「直接挂在该部门下的成员」
                      // 永远不会被加载。这里用 loadedDeptIds 做去重，兼顾叶子部门和有子部门的部门。
                      if (info.expanded && isDeptKey(info.node.key)) {
                        const deptId = parseDeptKey(info.node.key);
                        if (!loadedDeptIds.has(deptId) && !loadingDeptIds.has(deptId)) {
                          void loadDeptUsers(deptId);
                        }
                      }
                    }}
                    onCheck={(checked) => {
                      const rawKeys = Array.isArray(checked)
                        ? checked
                        : checked.checked;
                      // 「勾部门」=同时点亮其所有后代部门 + 所有已展示的成员叶子。
                      // 「取消部门」反之。已展开的成员叶子如果之前是单独勾选的，会被一并取消——
                      // 这是用户期望的：取消整个分支即取消该分支下所有可见项。
                      const next = new Set<Key>(rawKeys);
                      const prev = new Set<Key>(syncCheckedKeys);
                      for (const k of rawKeys) {
                        if (!prev.has(k) && isDeptKey(k)) {
                          const id = parseDeptKey(k);
                          for (const d of treeIndex.descendantDepts.get(id) ?? []) {
                            next.add(DEPT_KEY(d));
                          }
                          for (const uid of treeIndex.descendantUsers.get(id) ?? []) {
                            next.add(USER_KEY(uid));
                          }
                        }
                      }
                      for (const k of prev) {
                        if (!next.has(k) && isDeptKey(k)) {
                          const id = parseDeptKey(k);
                          for (const d of treeIndex.descendantDepts.get(id) ?? []) {
                            next.delete(DEPT_KEY(d));
                          }
                          for (const uid of treeIndex.descendantUsers.get(id) ?? []) {
                            next.delete(USER_KEY(uid));
                          }
                        }
                      }
                      setSyncCheckedKeys(Array.from(next));
                    }}
                  />
                </div>
              )}
              <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                已选 {checkedSummary.deptIds.length} 个部门
                {checkedSummary.userIds.length > 0
                  ? ` + ${checkedSummary.userIds.length} 名员工`
                  : ''}
              </div>
            </Form.Item>
          ) : (
            <Alert
              showIcon
              type="info"
              message={`将拉取${imLabel}里全部部门下的人员，与本地花名册比对`}
              description={`未在${imLabel}中出现且本地仍为「在职」的员工会被标记为「离职」（ADMIN 与无外部 ID 的手工号不动）。系统每天 02:00 也会自动跑一次。`}
            />
          )}
        </Form>
      </Modal>

      <Modal
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        title="导入花名册（Excel / CSV）"
        width={760}
        destroyOnClose
        footer={[
          <Button
            key="template"
            icon={<DownloadOutlined />}
            onClick={downloadImportTemplate}
            loading={templateBusy}
          >
            下载模版
          </Button>,
          <Button key="close" onClick={() => setImportOpen(false)}>
            关闭
          </Button>,
          <Button
            key="dry"
            onClick={() => callImport(false)}
            loading={importBusy}
            disabled={!importFile}
          >
            预览变更
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={() => callImport(true)}
            loading={importBusy}
            disabled={!importFile}
          >
            执行导入
          </Button>,
        ]}
      >
        <Alert
          showIcon
          style={{ marginBottom: 12 }}
          type="info"
          message="支持 .xlsx 与 .csv；建议先「预览变更」确认无误，再「执行导入」"
          description={
            <div style={{ fontSize: 12, color: '#666' }}>
              <div>
                <strong>列名容错</strong>（任一命中即可）：姓名（必填）、工号、部门路径 / 部门、
                门店 + 1级部门 + 2级部门（自动拼成 "门店/1级/2级"）、职位、序列、人才池、职级、等级、入职时间（"2026-04-22" 或 "2026年4月22日"）、
                直属上级工号 / 直属上级（姓名）、状态（在职/离职）、{imLabel} ID。
              </div>
              <div style={{ marginTop: 4 }}>
                <strong>匹配规则</strong>：按<strong>姓名</strong>匹配已有员工（工号不强制）；同名 ≥2 人须填工号消歧。
                新增员工缺「序列」时默认 PRO；缺「人才池」时默认「未分配」；缺「工号」时自动分配 MAN-* 工号。
              </div>
              <div style={{ marginTop: 4 }}>
                第一次使用？
                <Button
                  type="link"
                  size="small"
                  style={{ paddingInline: 4 }}
                  icon={<DownloadOutlined />}
                  onClick={downloadImportTemplate}
                  loading={templateBusy}
                >
                  下载导入模版
                </Button>
                （含示例行 + 字段说明 sheet）
              </div>
            </div>
          }
        />

        <Upload.Dragger
          multiple={false}
          accept=".xlsx,.csv"
          beforeUpload={(f) => {
            setImportFile({
              uid: String(Date.now()),
              name: f.name,
              status: 'done',
              originFileObj: f as any,
            });
            setImportPreview(null);
            return false;
          }}
          onRemove={() => {
            setImportFile(null);
            setImportPreview(null);
          }}
          fileList={importFile ? [importFile] : []}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽 Excel (.xlsx) / CSV (.csv) 文件到此处</p>
          <p className="ant-upload-hint">支持 .xlsx 与 .csv；解析失败的行会列在下方「警告」</p>
        </Upload.Dragger>

        {importPreview && (
          <div style={{ marginTop: 16 }}>
            <Space wrap>
              <Tag color="green">新增 {importPreview.added.length}</Tag>
              <Tag color="blue">更新 {importPreview.updated.length}</Tag>
              <Tag>无变化 {importPreview.unchanged.length}</Tag>
              <Tag color={importPreview.invalid.length ? 'red' : 'default'}>
                警告 {importPreview.invalid.length}
              </Tag>
              {importPreview.apply && importPreview.appliedCounts && (
                <Tag color="purple">
                  已写入：新增 {importPreview.appliedCounts.created} / 更新{' '}
                  {importPreview.appliedCounts.updated}
                </Tag>
              )}
            </Space>

            {importPreview.added.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 12 }}
                header={<b>新增（{importPreview.added.length}）</b>}
                bordered
                dataSource={importPreview.added.slice(0, 30)}
                renderItem={(it) => (
                  <List.Item>
                    {it.empNo} · {it.name}
                  </List.Item>
                )}
              />
            )}

            {importPreview.updated.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 12 }}
                header={<b>更新（{importPreview.updated.length}）</b>}
                bordered
                dataSource={importPreview.updated.slice(0, 30)}
                renderItem={(it) => (
                  <List.Item>
                    <div>
                      <div>
                        {it.empNo} · {it.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {it.changes.join('；')}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}

            {importPreview.invalid.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 12 }}
                header={<b style={{ color: '#cf1322' }}>警告（{importPreview.invalid.length}）</b>}
                bordered
                dataSource={importPreview.invalid.slice(0, 50)}
                renderItem={(it) => (
                  <List.Item>
                    第 {it.row} 行：{it.reason}
                  </List.Item>
                )}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function SyncDiffPreview({ diff, imLabel }: { diff: SyncResult; imLabel: string }) {
  const sections: Array<{
    title: string;
    color: string;
    rows: Array<{ key: string; main: string; sub?: string }>;
  }> = [
    {
      title: `新增 ${diff.added.length}`,
      color: '#52c41a',
      rows: diff.added.map((a) => ({
        key: `add-${a.userid}`,
        main: a.name,
        sub: [a.position, a.mobile].filter(Boolean).join(' · '),
      })),
    },
    {
      title: `变更 ${diff.changed.length}`,
      color: '#1677ff',
      rows: diff.changed.map((c) => ({
        key: `chg-${c.userid}`,
        main: `${c.before.name ?? '?'} → ${c.after.name}`,
        sub:
          c.before.mobile !== c.after.mobile
            ? `手机号 ${c.before.mobile ?? '空'} → ${c.after.mobile ?? '空'}`
            : '',
      })),
    },
    {
      title: `候选离职 ${diff.removed.length}`,
      color: '#ff4d4f',
      rows: diff.removed.map((r) => ({
        key: `rm-${r.userid}-${r.employeeId}`,
        main: r.name,
        sub: r.userid.startsWith('LOCAL:')
          ? '本地无外部 ID（老数据/补录），全量同步将标记为 LEFT'
          : `${imLabel}里已不存在，将标记为 LEFT`,
      })),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sections.map((s) => (
        <div key={s.title}>
          <div style={{ fontWeight: 600, color: s.color, marginBottom: 4 }}>{s.title}</div>
          {s.rows.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              无
            </Typography.Text>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7 }}>
              {s.rows.slice(0, 30).map((r) => (
                <li key={r.key}>
                  <span>{r.main}</span>
                  {r.sub ? <span style={{ color: '#888', marginLeft: 8 }}>{r.sub}</span> : null}
                </li>
              ))}
              {s.rows.length > 30 ? (
                <li style={{ color: '#888' }}>… 共 {s.rows.length} 条，省略后 {s.rows.length - 30} 条</li>
              ) : null}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * 把成员节点追加到指定部门节点的 children 末尾（保留已有子部门 + 标记 isLeaf=false）。
 * 不可变更新，避免触发不必要的重渲染。
 */
function appendUserNodes(
  tree: DataNode[],
  deptId: number,
  userNodes: DataNode[],
): DataNode[] {
  const targetKey = DEPT_KEY(deptId);
  const visit = (nodes: DataNode[]): DataNode[] =>
    nodes.map((n) => {
      if (n.key === targetKey) {
        const existingChildren = n.children ?? [];
        const existingUserKeys = new Set(
          existingChildren
            .filter((c) => typeof c.key === 'string' && c.key.startsWith('user-'))
            .map((c) => c.key),
        );
        const fresh = userNodes.filter((u) => !existingUserKeys.has(u.key));
        return {
          ...n,
          isLeaf: false,
          children: [...existingChildren, ...fresh],
        };
      }
      if (n.children && n.children.length > 0) {
        return { ...n, children: visit(n.children) };
      }
      return n;
    });
  return visit(tree);
}
